import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {IpopConfiguration} from '../../../../../model/ipop-configuration.model';

@Component({
    selector: 'auto-private-offers',
    templateUrl: './private-offers.component.html',
    styleUrls: ['./private-offers.component.scss'],
    standalone: false
})
export class PrivateOffersComponent implements OnInit, OnChanges {
  @Input() isReadOnly = true;
  @Input() data: IpopConfiguration | null = null;
  @Output() formGroupInitialized = new EventEmitter<UntypedFormGroup>();

  form: UntypedFormGroup;

  constructor(private formBuilder: UntypedFormBuilder) {
  }

  /** Initializes controls and applies the account lock before exposing the form. */
  ngOnInit(): void
  {
    this.form = this.formBuilder.group({
      includePrivateOffersInExternalAdvertising: [false, Validators.required],
      unlockPrivateOffersEnabled: [false, Validators.required],
      displaySavingsMessage: [true, Validators.required],
      savingsMessage: ['Additional Savings Unlocked', [Validators.required, Validators.maxLength(255)]],
      savingsMessageBackgroundColor: ['#DDE9E2', [Validators.required, Validators.maxLength(255)]],
      privateOfferTextColor: ['#000000', [Validators.required, Validators.maxLength(255)]],
      privateOfferButtonColor: ['#16A34A', [Validators.required, Validators.maxLength(255)]],
      showPriceComparison: [true, Validators.required],
      displayUnlockedSellingPrice: [false, Validators.required],
      preUnlockCtaText: ['Unlock Your Special Offer', Validators.required],
      postUnlockCtaText: ['Claim Your Special Offer', Validators.required],
      privateOfferDisclosure: ['']
    });
    this.applySettings();
    this.updateDisabledState();
    this.formGroupInitialized.emit(this.form);
  }

  /** Applies saved responses without overwriting edits when only the lock changes. */
  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.data) {
      this.applySettings();
    }
    this.updateDisabledState();
  }

  /** Displays saved values even when the tab mounts after its data has loaded. */
  private applySettings(): void
  {
    const hasIncoming = !!this.data && !!this.form;
    if (hasIncoming) {
      this.form.patchValue({
        includePrivateOffersInExternalAdvertising: !!this.data.includePrivateOffersInExternalAdvertising,
        unlockPrivateOffersEnabled: !!this.data.unlockPrivateOffersEnabled,
        displaySavingsMessage: this.data.displaySavingsMessage ?? true,
        savingsMessage: this.savedText(this.data.savingsMessage, 'Additional Savings Unlocked'),
        savingsMessageBackgroundColor: this.savedText(this.data.savingsMessageBackgroundColor, '#DDE9E2'),
        privateOfferTextColor: this.savedText(this.data.privateOfferTextColor, '#000000'),
        privateOfferButtonColor: this.savedText(this.data.privateOfferButtonColor, '#16A34A'),
        showPriceComparison: this.data.showPriceComparison ?? true,
        displayUnlockedSellingPrice: !!this.data.displayUnlockedSellingPrice,
        preUnlockCtaText: this.savedText(this.data.preUnlockCtaText, 'Unlock Your Special Offer'),
        postUnlockCtaText: this.savedText(this.data.postUnlockCtaText, 'Claim Your Special Offer'),
        privateOfferDisclosure: this.data.privateOfferDisclosure ?? ''
      }, {emitEvent: false});
      this.form.markAsPristine();
    }
  }

  /** Uses the existing display fallback only when a saved text value is empty. */
  private savedText(value: string | null | undefined, fallback: string): string
  {
    const text = value?.trim() || fallback;

    return text;
  }

  /** Changes only control availability, preserving all saved and unsaved values. */
  private updateDisabledState(): void
  {
    if (this.form) {
      if (this.isReadOnly || this.data === null) {
        this.form.disable({emitEvent: false});
      } else {
        this.form.enable({emitEvent: false});
      }
    }
  }

  /** Guards native color inputs, which do not inherit the reactive form lock. */
  setColor(controlName: string, event: Event): void
  {
    const control = this.form?.get(controlName);
    if (!this.isReadOnly && control?.enabled && event.target instanceof HTMLInputElement) {
      control.setValue(event.target.value);
      control.markAsDirty();
    }
  }
}
